import { ethers } from "ethers";
import { useEffect, useState } from "react";
import { Card, Heading, Box, Text, Image, Grid, Link, Spinner } from "theme-ui";
import { Statistic } from "./Statistic";
import * as l from "../lexicon";
import { TokenPrice } from "./TokenPrice";
import { Icon } from "./Icon";

import INCicon from "../assets/icons/INC.png";
import PRINTicon from "../assets/icons/PRINTtoken.png";
import INCDicon from "../assets/icons/INCDtoken.png";

import abi from "./Disconnected/abi";

import { getPrintPrice } from "../utils/tokensPrice";

type SystemStatsDisconnectedProps = {
  variant?: string;
};

export const SystemStatsDisconnected: React.FC<SystemStatsDisconnectedProps> = ({}) => {
  const provider = new ethers.providers.JsonRpcProvider(
    "https://rpc-pulsechain.g4mm4.io"
  );

  // #### NOTE! ####, change to mainnet url when launch.
  const mainnet =
    "https://scan.mypinata.cloud/ipfs/bafybeidn64pd2u525lmoipjl4nh3ooa2imd7huionjsdepdsphl5slfowy/#/";
  const pulsexapp =
    "https://app.piteas.io/#/swap?inputCurrency=PLS&outputCurrency=";

  // add token to wallet
  const addTokenToWallet = (
    tokenSymbol: string,
    tokenAddress: string,
    tokenImage: string
  ) => async () => {
    if (window.ethereum) {
      try {
        const { ethereum } = window as any;
        let url = "";

        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
          url = `http://localhost:3000/`;
        } else {
          url = `https://incprinter.com/#/`; // #### NOTE! ####, change to mainnet url when launch.
        }

        await ethereum.request({
          method: "wallet_watchAsset",
          params: {
            type: "ERC20",
            options: {
              address: tokenAddress,
              symbol: tokenSymbol,
              decimals: 18,
              image: url + tokenImage,
            },
          },
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  // #### NOTE! ####, change to mainnet url when launch.
  const plsxaddr = "0x2fa878Ab3F87CC1C9737Fc071108F904c0B0C95d";
  const pxdcToken = "0x144Cd22AaA2a80FEd0Bb8B1DeADDc51A53Df1d50";
  const earnaddr = "0x6C203A555824ec90a215f37916cf8Db58EBe2fA3";

  const earnStaking = "0x35b99f29b3Ec3276A2b3Bb5863326B1c100aa160";
  const troveManager = "0x248262ea52198643DD1512Ce7a2c93B32a03E45F";
  const activepools = "0xe77709d8c0e3685aC719E0DF10EE1FA887b190B0";
  const defaultpool = "0x14754BcA27589a6bF5ABCeF6bef1B95cc2dbb540";
  const stabilitypool = "0xfd2689b36E34453CB844637a7c8f1406A56eDE92";

  const plsxContract = new ethers.Contract(plsxaddr, abi.combined, provider);
  const earnStakingContract = new ethers.Contract(
    earnStaking,
    abi.combined,
    provider
  );
  const troveManagerContract = new ethers.Contract(
    troveManager,
    abi.combined,
    provider
  );
  const pxdcTokenContract = new ethers.Contract(
    pxdcToken,
    abi.combined,
    provider
  );

  const [loading, setLoading] = useState(true);
  const [earnStakeAmount, setEarnStakeAmount] = useState<any>("0");
  const [vaults, setVaults] = useState<any>("0");
  const [borrowRate, setBorrowRate] = useState<any>("0");
  const [tvl, setTvl] = useState<any>("0");
  const [tvlUsd, setTvlUsd] = useState<any>("0");
  const [mintedPxdc, setMintedPxdc] = useState<any>("0");
  const [poolPxdc, setPoolPxdc] = useState<any>("0");
  const [pxdcInStabilityPoolPct, setpxdcInStabilityPoolPct] = useState<number>(
    0
  );
  const [totalCollPct, setTotalCollPct] = useState<number>(0);

  // shorten function
  const shorten = (number: any, decimalPlaces: number) => {
    const roundedNumber = Math.ceil(number * 10) / 10; // Round up to one decimal place
    const suffixes = ["", "K", "M", "B", "T"];
    const magnitude = Math.floor(Math.log10(number) / 3);
    const formattedNumber = (
      roundedNumber / Math.pow(10, magnitude * 3)
    ).toFixed(decimalPlaces); // Fixed to one decimal place
    return formattedNumber + suffixes[magnitude];
  };

  // #### NOTE! ####, change to EARN ORACLE when launch.
  const [tokensPrice, setTokensPrice] = useState({
    PRINT: 0.0,
    INCD: 0.0, 
    INC: 0.0,
  });

  useEffect(() => {
    async function fetchData() {
      const response = await getPrintPrice();
      console.log(response);

      setTokensPrice(response);
      localStorage.setItem("tokensPrice", JSON.stringify(response));

      // Continue with the rest of the logic using tokenPrice.INC
      // Ensure tokenPrice.INC is defined before proceeding
      if (response.INC) {
        // tvl (active & default)
        const totalValueLockedActive = await plsxContract.balanceOf(
          activepools
        );
        const totalValueLockedDefault = await plsxContract.balanceOf(
          defaultpool
        );
        const tvlactive = ethers.utils.formatUnits(
          totalValueLockedActive.toString(),
          18
        );
        const tvldefault = ethers.utils.formatUnits(
          totalValueLockedDefault.toString(),
          18
        );
        const totalValueLocked = Number(tvlactive) + Number(tvldefault);

        setTvl(shorten(totalValueLocked, 1));

        // tvl usd
        setTvlUsd(shorten(Number(totalValueLocked) * Number(response.INC), 1));

        // total staked
        const totalEARNStaked = await earnStakingContract.totalLQTYStaked();
        setEarnStakeAmount(
          shorten(ethers.utils.formatUnits(totalEARNStaked.toString(), 18), 1)
        );

        // total minted
        const totalPXDCissued = await pxdcTokenContract.totalSupply();
        setMintedPxdc(
          shorten(ethers.utils.formatUnits(totalPXDCissued.toString(), 18), 2)
        );

        // total in pool
        const totalPXDCinpool = await pxdcTokenContract.balanceOf(
          stabilitypool
        );
        setPoolPxdc(
          shorten(ethers.utils.formatUnits(totalPXDCinpool.toString(), 18), 2)
        );

        // total debt
        const totalSystemdebt = await troveManagerContract.getEntireSystemDebt();

        // total system collateral
        const totalCollateral = await troveManagerContract.getEntireSystemColl();

        // PXDC In StabilityPool %
        setpxdcInStabilityPoolPct((totalPXDCinpool / totalSystemdebt) * 100);

        // total system collateral %
        setTotalCollPct(
          ((totalCollateral * Number(response.INC || 0)) / totalSystemdebt) *
            100
        );

        // tvl
        setTvl(shorten(totalValueLocked, 1));

        //total vaults
        const totalVaults = await troveManagerContract.getTroveOwnersCount();
        setVaults(totalVaults.toString());

        //get Borrowing Rate
        const borrowingRate = await troveManagerContract.getBorrowingRate();
        const percentage = (borrowingRate / 1e16).toFixed(2) + "%";
        setBorrowRate(percentage);

        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <>
      <Heading>INC Printer Statistics</Heading>

      {loading ? (
        <>
          <p>Loading...</p> <Spinner size="28px" sx={{ color: "text" }} />
        </>
      ) : (
        <>
          <Grid sx={{ mt: 30 }} gap={4} columns={[3]}>
            <Box sx={{ textAlign: "center" }}>
              <Image
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                src={INCicon}
                variant="avatar"
                onClick={addTokenToWallet("INC", plsxaddr, INCicon)}
              />
              <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
                INC
              </Heading>
              <Text sx={{ fontSize: 1, color: "#00afff" }}>
                ${tokensPrice.INC}
              </Text>
              <Text
                sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}
              >
                <TokenPrice lexicon={l.OPPRINT} />
                <Link href="#" sx={{ mr: 1 }}>
                  <Icon name="info-circle" />
                </Link>
                <Link href="#" sx={{ mr: 1 }}>
                  <Icon name="satellite" />
                </Link>
              </Text>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Image
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                src={PRINTicon}
                variant="avatar"
                onClick={addTokenToWallet("PRINT", earnaddr, PRINTicon)}
              />
              <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
                PRINT
              </Heading>
              <Text sx={{ fontSize: 1, color: "#00afff" }}>
                ${tokensPrice.PRINT}
              </Text>
              <Text
                sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}
              >
                <TokenPrice lexicon={l.MPPRINT} />
                <Link href="#" sx={{ mr: 1 }}>
                  <Icon name="info-circle" />
                </Link>
                <Link
                  target="_blank"
                  href={mainnet + "address/" + earnaddr}
                  sx={{ mr: 1 }}
                >
                  <Icon name="chart-bar" />
                </Link>
                <Link target="_blank" href={pulsexapp + earnaddr}>
                  <Icon name="exchange-alt" />
                </Link>
              </Text>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Image
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                src={INCDicon}
                variant="avatar"
                onClick={addTokenToWallet("INCD", pxdcToken, INCDicon)}
              />
              <Heading as="h3" sx={{ mt: 0, mb: 0, fontWeight: "body" }}>
                INCD
              </Heading>
              <Text sx={{ fontSize: 1, color: "#00afff" }}>
                ${tokensPrice.INCD}
              </Text>
              <Text
                sx={{ fontSize: 1, display: "block", mb: 15, fontWeight: 200 }}
              >
                <TokenPrice lexicon={l.MPINCD} />
                <Link href="#" sx={{ mr: 1 }}>
                  <Icon name="info-circle" />
                </Link>
                <Link
                  target="_blank"
                  href={mainnet + "address/" + pxdcToken}
                  sx={{ mr: 1 }}
                >
                  <Icon name="chart-bar" />
                </Link>
                <Link target="_blank" href={pulsexapp + pxdcToken}>
                  <Icon name="exchange-alt" />
                </Link>
              </Text>
            </Box>
          </Grid>

          <Heading as="h2" sx={{ mt: 3, fontWeight: "body" }}>
            Protocol
          </Heading>

          <Statistic lexicon={l.BORROW_FEE}>{borrowRate}</Statistic>

          <Statistic lexicon={l.TVL}>
            {tvl} <Text sx={{ fontSize: 1 }}>&nbsp;INC</Text>
            <Text sx={{ fontSize: 1 }}>&nbsp;(${tvlUsd})</Text>
          </Statistic>
          <Statistic lexicon={l.TROVES}>{vaults}</Statistic>
          <Statistic lexicon={l.LUSD_SUPPLY}>{mintedPxdc}</Statistic>

          <Statistic lexicon={l.STABILITY_POOL_LUSD}>
            {poolPxdc == "NaNundefined" ? "0" : poolPxdc}
            <Text sx={{ fontSize: 1 }}>
              &nbsp;({pxdcInStabilityPoolPct.toFixed(1)}%)
            </Text>
          </Statistic>

          <Statistic lexicon={l.STAKED_LQTY}>
            {earnStakeAmount == "NaNundefined" ? "0" : earnStakeAmount}
          </Statistic>
          <Statistic lexicon={l.TCR}>{totalCollPct.toFixed(1)}%</Statistic>
        </>
      )}
    </>
  );
};
